import { z } from 'zod'

/** Конверт сообщения. Всё входящее валидируется — доверия к серверу нет. */
export const wsEnvelopeSchema = z.object({
  type: z.string(),
  topic: z.string().optional(),
  payload: z.unknown().optional(),
  ts: z.number().optional(),
})

export type WsEnvelope = z.infer<typeof wsEnvelopeSchema>

export type WsStatus = 'idle' | 'connecting' | 'open' | 'closed'

export type WsHandler = (envelope: WsEnvelope) => void

/**
 * Коды закрытия приложения (4000–4999). Хендшейк и переход на вход — забота app-слоя:
 * клиент только сообщает код в `onClose`.
 * - `unauthorized` (4401) — тикет недействителен: на вход, сеанс сбросить (как baseQuery при 401);
 * - `forbidden` (4403) — соединение запрещено (в т.ч. `topic.forbidden`): без переподключения;
 * - `contextChanged` (4409) — сменилось активное юрлицо: переподписка, но не на вход.
 */
export const WS_CLOSE = {
  unauthorized: 4401,
  forbidden: 4403,
  contextChanged: 4409,
} as const

/** Пределы протокола, которые держит и бэкенд: ≤100 топиков, сообщение ≤32 КиБ. */
export const WS_LIMITS = {
  maxTopics: 100,
  maxMessageBytes: 32 * 1024,
} as const

export type WsClient = {
  connect: () => void
  disconnect: () => void
  subscribe: (topic: string, handler: WsHandler) => () => void
  send: (type: string, payload?: unknown, topic?: string) => void
  getStatus: () => WsStatus
  onStatusChange: (listener: (status: WsStatus) => void) => () => void
  /** Код закрытия сокета: 4401 → вход, 4403 → без реконнекта, 4409 → переподписка. */
  onClose: (listener: (code: number) => void) => () => void
  /** Сообщения уровня соединения без топика (`session.revoked`) — доставка подписчику app-слоя. */
  onEvent: (listener: (envelope: WsEnvelope) => void) => () => void
}

/** Подмена сокета и тикета в тестах: реального соединения нет. */
export type WsClientOptions = {
  createSocket?: (url: string) => WebSocket
  getTicket?: () => Promise<string | null>
  getUrl?: () => string
}
